import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { verifyEmail } from '@/api';
import AppLayout from '@/components/AppLayout';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const LOGO_URL = '/logo.png';

export default function VerifyEmail() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading, success, error

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }

    const verify = async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
        toast.success(t('auth.emailVerified'));
      } catch (err) {
        setStatus('error');
        if (err.response?.status === 400) {
          toast.error(t('auth.invalidToken'));
        } else {
          toast.error(t('common.error'));
        }
      }
    };

    verify();
  }, [token, t]);

  if (status === 'loading') {
    return (
      <AppLayout hideBrand>
        <div className="max-w-md mx-auto text-center space-y-4">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="ServiceGo" className="h-10" />
          </div>
          <Loader2 className="w-12 h-12 animate-spin text-[#5848FF] mx-auto" />
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </AppLayout>
    );
  }

  if (status === 'error') {
    return (
      <AppLayout hideBrand>
        <Toaster />
        <div className="max-w-md mx-auto text-center space-y-4">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="ServiceGo" className="h-10" />
          </div>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{t('auth.invalidToken')}</h2>
          <p className="text-gray-600">Il link di verifica potrebbe essere scaduto o non valido.</p>
          <Button 
            onClick={() => nav('/login')} 
            className="mt-6 !bg-[#5848FF] !text-white rounded-full h-11 px-8"
          >
            {t('auth.backToLogin')}
          </Button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout hideBrand>
      <Toaster />
      <div className="max-w-md mx-auto text-center space-y-4">
        <div className="flex justify-center mb-4">
          <img src={LOGO_URL} alt="ServiceGo" className="h-10" />
        </div>
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900">{t('auth.emailVerified')}</h2>
        <p className="text-gray-600">La tua email è stata verificata con successo. Ora puoi accedere a tutte le funzionalità.</p>
        <Button 
          onClick={() => nav('/login')} 
          className="mt-6 !bg-[#5848FF] !text-white rounded-full h-11 px-8"
        >
          {t('auth.login')}
        </Button>
      </div>
    </AppLayout>
  );
}
